import  { type FC, useEffect, useMemo, useRef, useState } from 'react';
import type { Delivery } from '../types';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../clients/supabaseClient';
import { fetchDeliveriesFromDb } from '../services/deliveryService.ts';
import StatusBadge from '../components/StatusBadge';
import TablePagination from "../components/TablePagination.tsx";

const ITEMS_PER_PAGE = 25;

const STATUS_OPTIONS = [
    { value: 'CREATED',           label: 'Created' },
    { value: 'AWAITING_PAYMENT',  label: 'Awaiting Payment' },
    { value: 'AWAITING_PACKING',  label: 'Awaiting Packing' },
    { value: 'PACKER_ASSIGNED',   label: 'Packer Assigned' },
    { value: 'ASSIGN_DRIVER',     label: 'Assign Driver' },
    { value: 'DRIVER_ASSIGNED',   label: 'Driver Assigned' },
    { value: 'ON_ROUTE',          label: 'On Route' },
    { value: 'DELIVERY_COMPLETE', label: 'Delivery Complete' },
    { value: 'DELIVERY_FAILED',   label: 'Failed' },
    { value: 'ORDER_CANCELLED',   label: 'Cancelled' },
];

const STATUS_LABEL_MAP: Record<string, string> = Object.fromEntries(
    STATUS_OPTIONS.map(s => [s.value, s.label])
);

const getStatusLabel = (status?: string | null) => {
    if (!status) return '—';
    return STATUS_LABEL_MAP[status] ?? status.replace(/_/g, ' ');
};

const Deliveries: FC = () => {
    const navigate = useNavigate();
    const statusDropdownRef = useRef<HTMLDivElement | null>(null);

    const [deliveries, setDeliveries] = useState<Delivery[]>([]);
    const [deliveryTab, setDeliveryTab] = useState<'open' | 'delivered'>('open');
    const [loading, setLoading] = useState(true);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const loadDeliveries = async () => {
        try {
            setLoading(true);
            const data = await fetchDeliveriesFromDb();
            if (data) setDeliveries(data as Delivery[]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadDeliveries();

        const channel = supabase
            .channel('deliveries-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => {
                void loadDeliveries();
            })
            .subscribe();

        return () => { void supabase.removeChannel(channel); };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { setStatusFilters([]); setIsStatusDropdownOpen(false); }, [deliveryTab]);
    useEffect(() => { setCurrentPage(1); }, [deliveryTab, statusFilters]);

    const openDeliveries = useMemo(() =>
        deliveries.filter(d => {
            const s = d.status?.toUpperCase();
            return s !== 'DELIVERY_COMPLETE' && s !== 'COMPLETED';
        }), [deliveries]);

    const deliveredDeliveries = useMemo(() =>
        deliveries.filter(d => {
            const s = d.status?.toUpperCase();
            return s === 'DELIVERY_COMPLETE' || s === 'COMPLETED';
        }), [deliveries]);

    const baseDeliveries = useMemo(() =>
            deliveryTab === 'delivered' ? deliveredDeliveries : openDeliveries,
        [deliveryTab, deliveredDeliveries, openDeliveries]);

    const statusOptions = useMemo(() => {
        const existing = new Set(
            baseDeliveries.map(d => d.status?.toUpperCase()).filter((s): s is string => Boolean(s))
        );
        return STATUS_OPTIONS.filter(o => existing.has(o.value));
    }, [baseDeliveries]);

    const filteredDeliveries = useMemo(() => {
        if (deliveryTab === 'delivered' || statusFilters.length === 0) return baseDeliveries;
        return baseDeliveries.filter(d => {
            const s = d.status?.toUpperCase();
            return s ? statusFilters.includes(s) : false;
        });
    }, [baseDeliveries, statusFilters, deliveryTab]);

    const totalItems  = filteredDeliveries.length;
    const totalPages  = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const startIndex  = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedDeliveries = filteredDeliveries.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };

    const toggleStatusFilter = (status: string) =>
        setStatusFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);

    const clearStatusFilters = () => setStatusFilters([]);

    const switchToOpenTab      = () => { setDeliveryTab('open');      setStatusFilters([]); setIsStatusDropdownOpen(false); };
    const switchToDeliveredTab = () => { setDeliveryTab('delivered'); setStatusFilters([]); setIsStatusDropdownOpen(false); };

    const goToDelivery = (delivery: Delivery) => {
        const orderId = delivery.order?.id ?? (delivery as Delivery & { order_id?: string }).order_id;
        if (orderId) navigate(`/orders/${orderId}`, { state: { activeTab: 'deliveries' } });
    };

    const statusFilterLabel =
        statusFilters.length === 0 ? 'All Statuses'
            : statusFilters.length === 1 ? getStatusLabel(statusFilters[0])
                : `${statusFilters.length} statuses selected`;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-stone-900">Deliveries</h1>
                <p className="mt-1 text-sm text-stone-500">Monitor and manage delivery progress across all active orders</p>
            </div>

            {/* Tabs */}
            <div className="flex">
                <div className="inline-flex overflow-hidden rounded-lg border border-stone-300 bg-white shadow-sm">
                    <button type="button" onClick={switchToOpenTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${deliveryTab === 'open' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Open
                    </button>
                    <button type="button" onClick={switchToDeliveredTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${deliveryTab === 'delivered' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Delivered
                    </button>
                </div>
            </div>

            {/* Status filter */}
            {deliveryTab === 'open' && (
                <div ref={statusDropdownRef} className="relative inline-block w-full sm:w-64">
                    <button type="button" onClick={() => setIsStatusDropdownOpen(p => !p)}
                            className="flex w-full items-center justify-between rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <span className="truncate text-left">{statusFilterLabel}</span>
                        <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {isStatusDropdownOpen && (
                        <div className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-stone-200 bg-white shadow-lg">
                            <div className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Filter by status</span>
                                <button type="button" onClick={clearStatusFilters}
                                        className="text-xs font-medium text-[#ee2b74] hover:underline">Clear</button>
                            </div>
                            <div className="max-h-72 overflow-y-auto py-2">
                                {statusOptions.map(status => (
                                    <label key={status.value}
                                           className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
                                        <input type="checkbox" checked={statusFilters.includes(status.value)}
                                               onChange={() => toggleStatusFilter(status.value)}
                                               className="h-4 w-4 rounded border-stone-300 accent-[#ee2b74]" />
                                        <span>{status.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-stone-200 bg-stone-50">
                        <tr>
                            {['Order Ref', 'Customer', 'Driver', 'Status', ''].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {loading ? (
                            Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td className="px-4 py-4"><div className="h-4 w-24 rounded bg-stone-100" /></td>
                                    <td className="px-4 py-4"><div className="h-4 w-40 rounded bg-stone-100" /></td>
                                    <td className="px-4 py-4"><div className="h-4 w-32 rounded bg-stone-100" /></td>
                                    <td className="px-4 py-4"><div className="h-6 w-20 rounded-full bg-stone-100" /></td>
                                    <td className="px-4 py-4"><div className="h-5 w-5 rounded bg-stone-100" /></td>
                                </tr>
                            ))
                        ) : filteredDeliveries.length === 0 ? (
                            <tr>
                                <td className="px-4 py-8 text-center text-sm text-stone-500" colSpan={5}>
                                    {deliveryTab === 'delivered' ? 'No delivered deliveries found.' : 'No deliveries found.'}
                                </td>
                            </tr>
                        ) : (
                            paginatedDeliveries.map(delivery => (
                                <tr key={delivery.id} onClick={() => goToDelivery(delivery)}
                                    className="group cursor-pointer transition hover:bg-stone-50">
                                    <td className="px-4 py-3 font-medium text-stone-900">
                                        {delivery.order?.reference ?? '—'}
                                    </td>
                                    <td className="px-4 py-3 text-stone-700">
                                        {delivery.customer?.first_name ?? 'Unknown'}{' '}
                                        {delivery.customer?.last_name ?? ''}
                                    </td>
                                    <td className="px-4 py-3 text-stone-600">
                                        {delivery.driver?.driver_name ?? '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={delivery.status ?? ''} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <ChevronRightIcon className="h-5 w-5 text-stone-400 group-hover:text-stone-500 transition" />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalItems > 0 && (
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default Deliveries;